/**
 * Panneau de droite : fiche de l'objet sélectionné (remontée, piste,
 * bâtiment) avec sa jauge de charge, ses quotas du moment et l'historique
 * de ses quarts d'heure ; sans sélection, l'affluence en direct de toutes
 * les installations, triées de la plus chargée à la plus calme.
 */
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { Power, Snowflake, Trash2, Wrench, X } from 'lucide-preact';
import {
  BUILDINGS,
  CLOSED,
  DAY_SCALE,
  DIFFICULTY_LABEL,
  LIFTS,
  USAGE_BUCKETS,
  bucketRange,
  buildingName,
  calendarDay,
  findJams,
  summarizeFacility,
  type Building,
  type FacilityMeta,
  type Lift,
  type Piste,
} from '@snowchain/shared';
import { Button, HudPanel } from './components';
import { command, focusEntity, hud } from './context';
import { euros, n0 } from './format';
import { DIFF_COLOR, FACILITY_ICON, LIFT_STATUS, buildingIcon, levelColor, levelLabel } from './labels';
import { facilityKeys, liveFacilities, type LiveFacility } from './live';
import { Fact, LevelChip, QuotaRow, Tabs } from './game-ui';
import { Gauge, HourAxis, LevelBar, UsageBars, UsageStrip } from './charts';
import { currentBucket, dayLabel, describeBucket, describeJam, jamShort, todayIndex, usageDay, waitText } from './report';
import { ui, type LiveTab } from './ui-state';
import { GarageFleet, GroomerCard, GroomingPanel, PisteGrooming } from './grooming';
import { PisteManage } from './pistes';
import { buildingCat, catMeta, liftIcon, thumbUrl } from './build-cats';
import type { AnyIcon } from './icons';

const LIVE_TABS: { id: LiveTab; label: string }[] = [
  { id: 'remontees', label: 'Remontées' },
  { id: 'batiments', label: 'Bâtiments' },
  { id: 'pistes', label: 'Pistes' },
];

function select(f: LiveFacility): void {
  if (f.ref) focusEntity(f.ref.kind, f.ref.id);
  if (f.ref?.kind === 'building') ui.inspectorService.value = f.key;
}

/** Affluence en direct : chaque installation avec sa charge et sa valeur clé. */
function LivePanel() {
  const store = hud.store!;
  const r = store.replica!;
  const all = liveFacilities(r, store.map.features.village);
  const tab = ui.liveTab.value;
  const list = all
    .filter((f) => (tab === 'remontees' ? f.kind === 'lift' : tab === 'pistes' ? f.kind === 'piste' : f.kind !== 'lift' && f.kind !== 'piste'))
    .sort((a, b) => b.level - a.level || b.u - a.u);
  const jams = all.filter((f) => f.level >= 2);
  const count = (k: LiveTab) => all.filter((f) => f.level >= 2 && (k === 'remontees' ? f.kind === 'lift' : k === 'pistes' ? f.kind === 'piste' : f.kind !== 'lift' && f.kind !== 'piste')).length;
  return (
    <HudPanel class="g-panel g-inspector" title="Affluence en direct">
      <div class={`g-live-summary${jams.length ? ' g-live-alert' : ''}`}>
        {jams.length ? (
          <>
            <LevelChip level={Math.max(...jams.map((j) => j.level))} label={`${jams.length} point${jams.length > 1 ? 's' : ''} saturé${jams.length > 1 ? 's' : ''}`} />
            <span class="g-sub">{jams.slice(0, 2).map((j) => j.label).join(', ')}</span>
          </>
        ) : (
          <>
            <LevelChip level={0} label="Tout est fluide" />
            <span class="g-sub">Aucune file à surveiller</span>
          </>
        )}
      </div>
      <Tabs value={tab} small options={LIVE_TABS.map((t) => ({ ...t, badge: count(t.id) || undefined }))} onChange={(v) => (ui.liveTab.value = v)} />
      <div class="g-live-list">
        {list.map((f) => {
          const Icon = f.kind === 'lift' && f.ref ? liftIcon(r.lifts.get(f.ref.id)?.kind ?? 'telesiege') : FACILITY_ICON[f.kind];
          return (
            <button type="button" class="g-live-row" key={f.key} onClick={() => select(f)} title={`${f.label} : ${levelLabel(f.level)}`}>
              <span class="g-live-dot" style={{ background: levelColor(f.level) }} />
              <Icon size={14} />
              <span class="g-live-name">{f.label}</span>
              <LevelBar u={f.level === CLOSED ? 0 : f.u} level={f.level} />
              <span class="g-live-val num">{f.short}</span>
            </button>
          );
        })}
        {!list.length && <p class="g-empty">Rien à afficher ici pour l'instant.</p>}
      </div>
    </HudPanel>
  );
}

/** Ruban du jour d'une installation, avec la plage la plus chargée. */
function TodayStrip(props: { fkey: string }) {
  const store = hud.store!;
  const r = store.replica!;
  const day = usageDay(r.usage, null);
  const meta = day?.facilities.find((f) => f.key === props.fkey);
  const [hover, setHover] = useState<number | null>(null);
  if (!r.usage) return null;
  if (!day || !meta) return <p class="g-empty">Mesures disponibles au prochain quart d'heure.</p>;
  const s = day.series[meta.key];
  const now = currentBucket(store.clock.gameNow());
  const worst = findJams(meta.kind, s, day.n)[0];
  return (
    <div class="g-today">
      <div class="g-section-title">
        Aujourd'hui, par quart d'heure
        <button class="g-link" onClick={() => (ui.inspectorTab.value = 'historique')}>
          Historique
        </button>
      </div>
      <HourAxis now={now} />
      <UsageStrip kind={meta.kind} series={s} n={day.n} hover={hover} onHover={setHover} now={now} />
      <p class="g-info-line">
        {hover !== null ? describeBucket(meta, s, hover) : worst ? `Pire moment : ${bucketRange(worst.start, worst.end)} · ${levelLabel(worst.level)} · ${describeJam(worst, meta.kind)}` : 'Aucun bouchon aujourd\u2019hui.'}
      </p>
    </div>
  );
}

/** Historique détaillé d'une installation : journées, barres, épisodes, saison. */
function FacilityHistory(props: { fkey: string }) {
  const store = hud.store!;
  const r = store.replica!;
  const usage = r.usage;
  const today = todayIndex(store.clock.gameNow());
  const [dayNo, setDayNo] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  if (!usage) return <p class="g-empty">L'historique est réservé au gérant de la station.</p>;
  const days = [...usage.days].reverse();
  const day = dayNo === null ? days[0] : (days.find((d) => d.day === dayNo) ?? days[0]);
  const meta: FacilityMeta | undefined = day?.facilities.find((f) => f.key === props.fkey);
  const s = meta && day ? day.series[meta.key] : undefined;
  const sum = meta && day ? summarizeFacility(meta, s, day.n) : null;
  const jams = meta && day ? findJams(meta.kind, s, day.n) : [];
  const season = usage.summaries.slice(-14).map((d) => ({ day: d.day, f: d.facilities.find((f) => f.key === props.fkey) }));
  return (
    <div class="g-history">
      <div class="g-daychips">
        {days.map((d) => (
          <button key={d.day} class={d === day ? 'on' : ''} onClick={() => setDayNo(d.day)}>
            {dayLabel(d.day, today)}
          </button>
        ))}
      </div>
      {!meta || !day ? (
        <p class="g-empty">Pas de mesure ce jour-là pour cette installation.</p>
      ) : (
        <>
          <UsageBars kind={meta.kind} series={s} n={day.n} hover={hover} onHover={setHover} now={day.day === today ? currentBucket(store.clock.gameNow()) : null} />
          <p class="g-info-line">{hover !== null ? describeBucket(meta, s, hover) : `${day.n}/${USAGE_BUCKETS} quarts d'heure mesurés. Survolez une barre pour le détail.`}</p>
          {sum && (
            <div class="g-facts g-facts-2">
              <Fact label="Utilisation moyenne" value={`${n0(sum.avgU)} %`} />
              <Fact label="Pic" value={`${n0(sum.peakU)} %`} />
              {meta.kind !== 'piste' && meta.kind !== 'food' && meta.kind !== 'accueil' && <Fact label="Attente max" value={sum.maxW > 0 ? waitText(sum.maxW) : 'aucune'} />}
              <Fact label="Servis" value={n0(sum.served)} />
              {meta.kind !== 'lift' && meta.kind !== 'piste' && <Fact label="Perdus" value={n0(sum.lost)} tone={sum.lost > 0 ? 'danger' : undefined} />}
              <Fact label="Temps saturé" value={sum.jamMin ? `${n0(sum.jamMin)} min` : 'aucun'} tone={sum.jamMin >= 60 ? 'warn' : undefined} />
            </div>
          )}
          <div class="g-section-title">Bouchons de la journée</div>
          {jams.length ? (
            <ul class="g-jams">
              {jams.slice(0, 4).map((j) => (
                <li key={j.start}>
                  <LevelChip level={j.level} />
                  <b>{bucketRange(j.start, j.end)}</b>
                  <span class="g-sub">{describeJam(j, meta.kind)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p class="g-empty">Aucun bouchon ce jour-là.</p>
          )}
        </>
      )}
      {season.length > 0 && (
        <>
          <div class="g-section-title">Journées précédentes (temps saturé)</div>
          <div class="g-season-row">
            {season.map(({ day: d, f }) => (
              <span key={d} class="g-season-day" title={f ? `${dayLabel(d, today)} : ${levelLabel(f.level)}, pic ${n0(f.peakU)} %, ${f.jamMin} min saturé, ${n0(f.lost)} perdus` : `${dayLabel(d, today)} : pas de mesure`}>
                <span class="g-season-cell" style={{ background: levelColor(f ? f.level : null) }}>
                  {f ? jamShort(f.jamMin) : ''}
                </span>
                <span class="g-season-date">{calendarDay(d).dayOfMonth}</span>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** En-tête de fiche : vignette 3D du modèle (ou pictogramme sur pastille de sa rubrique), nom, état. */
function CardHeader(props: { icon: AnyIcon; level: number; title: string; subtitle: string; onClose: () => void; thumb?: string | null; color?: string }) {
  const Icon = props.icon;
  return (
    <div class="g-card-head" style={props.color ? ({ '--cat': props.color } as Record<string, string>) : undefined}>
      {props.thumb ? (
        <span class="g-card-thumb">
          <img src={props.thumb} alt="" draggable={false} />
        </span>
      ) : (
        <span class="g-card-ic">
          <Icon size={22} />
        </span>
      )}
      <div class="g-card-head-text">
        <div class="g-card-title">{props.title}</div>
        <div class="g-card-sub">
          <i class="g-card-dot" style={{ background: levelColor(props.level) }} />
          <span class="g-sub">{props.subtitle}</span>
        </div>
      </div>
      <button class="sc-ibtn sc-ibtn-sm sc-ibtn-ghost" aria-label="Fermer" onClick={props.onClose}>
        <X size={14} />
      </button>
    </div>
  );
}

function InspectorShell(props: { header: JSX.Element; overview: JSX.Element; fkey: string | null; services?: JSX.Element | null }) {
  const tab = ui.inspectorTab.value;
  const owner = !!hud.store?.replica?.usage;
  return (
    <section class="sc-hud g-panel g-inspector">
      {props.header}
      {props.fkey && owner && (
        <Tabs
          value={tab}
          small
          options={[
            { id: 'apercu', label: 'Aperçu' },
            { id: 'historique', label: 'Historique' },
          ]}
          onChange={(v) => (ui.inspectorTab.value = v)}
        />
      )}
      <div class="sc-hud-body g-inspector-body">
        {props.services}
        {tab === 'historique' && props.fkey && owner ? <FacilityHistory fkey={props.fkey} /> : props.overview}
      </div>
    </section>
  );
}

const close = () => {
  ui.selection.value = null;
  ui.inspectorTab.value = 'apercu';
};

function LiftCard(props: { lift: Lift; live: LiveFacility | undefined }) {
  const l = props.lift;
  const f = props.live;
  const spec = LIFTS[l.kind];
  const st = LIFT_STATUS[l.status];
  const owner = hud.store?.isOwner;
  const r = hud.store!.replica!;
  // Descentes depuis son sommet : pistes qui en partent, ou qu'il rejoint en cours de route.
  const served = new Set((hud.store?.network()?.options(`T${l.id}`) ?? []).map((o) => o.piste));
  const pistes = [...r.pistes.values()].filter((p) => p.from === `T${l.id}` || served.has(p.id));
  const level = f?.level ?? CLOSED;
  const overview = (
    <>
      <div class="g-overview">
        <Gauge value={f?.u ?? 0} level={level} label={l.status === 'open' ? levelLabel(level) : st.label} />
        <div class="g-overview-facts">
          {l.status === 'construction' ? (
            <Fact label="Chantier" value={`${n0(l.progress * 100)} %`} />
          ) : (
            <>
              <QuotaRow label="Débit" used={l.throughput} cap={f?.rate ?? 0} unit="/h" level={level} hint={`Personnes embarquées par heure réelle (mesuré sur les dix dernières minutes) et débit nominal. L'horloge du jeu tourne ${DAY_SCALE} fois plus vite.`} />
              <Fact label="File d'attente" value={l.queue > 0 ? `${n0(l.queue)} pers. · ${waitText(l.waitSec)}` : 'personne'} tone={l.waitSec >= 300 ? 'danger' : l.waitSec >= 150 ? 'warn' : undefined} />
              <Fact label="Passages aujourd'hui" value={n0(l.rides ?? 0)} />
              <Fact label="Usure" value={`${n0(l.wear * 100)} %`} tone={l.wear >= 0.7 ? 'warn' : undefined} />
            </>
          )}
        </div>
      </div>
      {pistes.map((p) => (
        <button type="button" class="g-mini-row" key={p.id} onClick={() => focusEntity('piste', p.id)}>
          <span class="g-live-dot" style={{ background: DIFF_COLOR[p.difficulty] }} />
          <span class="g-live-name">
            {DIFFICULTY_LABEL[p.difficulty]} · {p.name}
          </span>
          <span class="g-live-val">{p.open ? `${n0(p.snowDepth)} cm` : (p.closedReason ?? 'Fermée')}</span>
        </button>
      ))}
      {l.status !== 'construction' && <TodayStrip fkey={`L${l.id}`} />}
      {owner && l.status !== 'construction' && (
        <div class="kh-actions">
          {l.status === 'broken' && l.repairUntil === null && (
            <Button size="sm" onClick={() => void command({ type: 'repairLift', id: l.id }, 'Réparation lancée')}>
              <Wrench size={14} /> Réparer ({euros(spec.repairCost)})
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => void command({ type: 'setLiftOpen', id: l.id, open: l.manualClosed })}>
            <Power size={14} /> {l.manualClosed ? 'Rouvrir' : 'Fermer'}
          </Button>
          {l.manualClosed && l.queue === 0 && (
            <Button size="sm" variant="ghost" onClick={() => void command({ type: 'demolish', id: l.id }, 'Remontée démolie')}>
              <Trash2 size={14} /> Démolir
            </Button>
          )}
        </div>
      )}
    </>
  );
  return (
    <InspectorShell
      fkey={l.status === 'construction' ? null : `L${l.id}`}
      header={<CardHeader icon={liftIcon(l.kind)} thumb={thumbUrl(`l:${l.kind}`)} color={catMeta('lift').color} level={level} title={l.name} subtitle={`${spec.label} · ${st.label}`} onClose={close} />}
      overview={overview}
    />
  );
}

function PisteCard(props: { piste: Piste; live: LiveFacility | undefined }) {
  const p = props.piste;
  const f = props.live;
  const owner = hud.store?.isOwner;
  const level = f?.level ?? CLOSED;
  const tone = !p.open ? 'danger' : p.snowDepth < 40 ? 'warn' : 'ok';
  const overview = (
    <>
      <div class="g-overview">
        <Gauge value={f?.u ?? 0} level={level} label={p.open ? levelLabel(level) : 'Fermée'} />
        <div class="g-overview-facts">
          <QuotaRow label="Skieurs sur la piste" used={p.traffic} cap={f?.quota?.cap ?? 0} level={level} hint="Skieurs en même temps, comparés au nombre qu'elle accueille sans gêne (un pour 350 m²)" />
          <Fact label="Enneigement" value={`${n0(p.snowDepth)} cm`} tone={tone === 'ok' ? undefined : tone} />
          <Fact label="Qualité de neige" value={`${n0(p.quality * 100)} %`} tone={p.quality < 0.4 ? 'warn' : undefined} />
          <Fact label="Enneigeurs" value={`${n0(p.cannons)}${p.snowmaking ? ' en marche' : ''}`} />
        </div>
      </div>
      <p class="g-sub g-wrap">
        {n0(p.length)} m · {n0(p.drop)} m de dénivelé · largeur {n0(p.width)} m
      </p>
      {p.closedReason !== 'En construction' && <TodayStrip fkey={`P${p.id}`} />}
      <PisteGrooming piste={p} />
      <PisteManage piste={p} />
      {owner && (
        <div class="kh-actions">
          {p.cannons > 0 && (
            <Button size="sm" variant={p.snowmaking ? 'secondary' : 'primary'} onClick={() => void command({ type: 'setSnowmaking', id: p.id, on: !p.snowmaking })}>
              <Snowflake size={14} /> {p.snowmaking ? 'Arrêter la neige de culture' : 'Neige de culture'}
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => void command({ type: 'setPisteOpen', id: p.id, open: p.manualClosed })}>
            <Power size={14} /> {p.manualClosed ? 'Rouvrir' : 'Fermer'}
          </Button>
        </div>
      )}
    </>
  );
  return (
    <InspectorShell
      fkey={p.closedReason === 'En construction' ? null : `P${p.id}`}
      header={<CardHeader icon={FACILITY_ICON.piste} color={DIFF_COLOR[p.difficulty]} level={level} title={`${DIFFICULTY_LABEL[p.difficulty]} · ${p.name}`} subtitle={p.open ? 'Ouverte' : (p.closedReason ?? 'Fermée')} onClose={close} />}
      overview={overview}
    />
  );
}

const SERVICE_LABEL: Record<string, string> = { ticket: 'Caisses', rental: 'Location', food: 'Restaurant', accueil: 'Parking' };

/** Quotas d'un service de bâtiment. */
function ServiceView(props: { b: Building; f: LiveFacility }) {
  const { b, f } = props;
  const spec = BUILDINGS[b.kind];
  const r = hud.store!.replica!;
  if (f.kind === 'ticket' || f.kind === 'rental') {
    return (
      <div class="g-overview">
        <Gauge value={f.u} level={f.level} label={levelLabel(f.level)} />
        <div class="g-overview-facts">
          {f.kind === 'rental' && f.quota && <QuotaRow label="Parc de skis loué" used={f.quota.used} cap={f.quota.cap} unit="paires" hint="Paires sorties aujourd'hui sur le parc de la boutique (rendues le soir)" />}
          <Fact label="File" value={f.queue > 0 ? `${n0(f.queue)} pers. · ${waitText(f.wait)}` : 'personne'} tone={f.wait >= 120 ? 'danger' : f.wait >= 60 ? 'warn' : undefined} />
          <Fact label={f.kind === 'ticket' ? "Forfaits vendus aujourd'hui" : "Clients équipés aujourd'hui"} value={n0(f.served)} />
          <Fact label="Débit du comptoir" value={`${n0(f.rate ?? 0)} /h`} title="Clients servis par heure de jeu quand la file ne désemplit pas" />
          <Fact label={f.kind === 'ticket' ? 'Visiteurs repartis' : 'Clients perdus'} value={n0(f.lost)} tone={f.lost > 0 ? 'danger' : undefined} />
        </div>
      </div>
    );
  }
  if (f.kind === 'food') {
    return (
      <div class="g-overview">
        <Gauge value={f.u} level={f.level} label={levelLabel(f.level)} />
        <div class="g-overview-facts">
          {f.quota && <QuotaRow label="Places occupées" used={f.quota.used} cap={f.quota.cap} unit="places" />}
          <Fact label="Repas servis aujourd'hui" value={n0(f.served)} />
          <Fact label="Clients refoulés" value={n0(f.lost)} tone={f.lost > 0 ? 'danger' : undefined} title="Salle pleine ou denrées épuisées" />
          <Fact label="Prix moyen du repas" value={euros(spec.mealPrice ?? 15)} />
        </div>
      </div>
    );
  }
  const s = r.station.stats;
  return (
    <div class="g-overview">
      <Gauge value={f.u} level={f.level} label={levelLabel(f.level)} />
      <div class="g-overview-facts">
        <QuotaRow label="Visiteurs présents (toute la station)" used={s.onDomain} cap={s.parkingCapacity} unit="places" level={f.level} />
        <Fact label="Places apportées ici" value={`+${n0(spec.parking ?? 0)}`} />
        <Fact label="Refusés aujourd'hui" value={n0(s.missed?.parking ?? 0)} tone={(s.missed?.parking ?? 0) > 0 ? 'danger' : undefined} title="Visiteurs arrivés quand le parking était plein" />
      </div>
    </div>
  );
}

/** Effets du bâtiment lus dans le catalogue (quotas journaliers compris). */
function BuildingEffects(props: { b: Building }) {
  const b = props.b;
  const spec = BUILDINGS[b.kind];
  const rows: JSX.Element[] = [];
  if (spec.beds) rows.push(<QuotaRow key="beds" label="Clients de la nuit" used={b.served} cap={spec.beds} unit="lits" hint={`${euros(spec.roomPrice ?? 0)} la nuitée par lit occupé`} />);
  if (spec.lessonsPerDay) rows.push(<QuotaRow key="lessons" label="Cours vendus aujourd'hui" used={b.served} cap={spec.lessonsPerDay} unit="cours" hint={`${euros(spec.lessonPrice ?? 0)} le cours collectif`} />);
  if (spec.waterPerHour) rows.push(<Fact key="water" label="Eau consommée en marche" value={`${n0(spec.waterPerHour)} m³/h`} />);
  if (spec.waterCapacity) rows.push(<Fact key="wcap" label="Retenue d'eau ajoutée" value={`+${n0(spec.waterCapacity)} m³`} />);
  if (spec.waterInflowPerHour) rows.push(<Fact key="winf" label="Remplissage naturel ajouté" value={`+${n0(spec.waterInflowPerHour)} m³/h`} />);
  if (spec.groomers) rows.push(<Fact key="groom" label="Dameuses" value={`${n0(spec.groomers)}, sorties chaque nuit`} />);
  if (spec.rescue) rows.push(<Fact key="rescue" label="Évacuations" value="moins pénibles" />);
  if (spec.ambiance) rows.push(<Fact key="amb" label="Ambiance" value={`+${spec.ambiance.toLocaleString('fr-FR')} pt de satisfaction`} />);
  rows.push(<Fact key="upkeep" label="Entretien" value={`${euros(spec.upkeepPerDay)}/j`} />);
  return <div class="g-facts">{rows}</div>;
}

function BuildingCard(props: { b: Building; live: LiveFacility[] }) {
  const b = props.b;
  const r = hud.store!.replica!;
  const spec = BUILDINGS[b.kind];
  const keys = facilityKeys(r, { kind: 'building', id: b.id });
  const services = keys.map((k) => props.live.find((f) => f.key === k)).filter((f): f is LiveFacility => !!f);
  const chosen = services.find((f) => f.key === ui.inspectorService.value) ?? services[0];
  const level = chosen?.level ?? 0;
  const name = buildingName(b, r.buildings.values());
  const owner = hud.store?.isOwner;
  const overview = (
    <>
      {b.status !== 'open' && (
        <div class="g-overview">
          <Gauge value={b.progress} level={0} label="Chantier" />
          <p class="g-sub g-wrap">{spec.description}</p>
        </div>
      )}
      {b.status === 'open' && chosen && <ServiceView b={b} f={chosen} />}
      {b.status === 'open' && (
        <>
          <p class="g-sub g-wrap">{spec.description}</p>
          <BuildingEffects b={b} />
          <GarageFleet b={b} />
        </>
      )}
      {b.status === 'open' && chosen && <TodayStrip fkey={chosen.key} />}
      {owner && (
        <div class="kh-actions">
          <Button size="sm" variant="ghost" onClick={() => void command({ type: 'demolish', id: b.id }, 'Démoli')}>
            <Trash2 size={14} /> Démolir (rembourse 25 %)
          </Button>
        </div>
      )}
    </>
  );
  const sub = b.status === 'open' ? (chosen ? `${SERVICE_LABEL[chosen.kind] ?? spec.label} · ${chosen.status}` : 'En service') : `Chantier ${n0(b.progress * 100)} %`;
  // Chalet : caisses, restaurant et parking, au choix dans l'aperçu comme dans l'historique.
  const serviceTabs =
    b.status === 'open' && services.length > 1 && chosen ? (
      <Tabs value={chosen.key} small options={services.map((f) => ({ id: f.key, label: SERVICE_LABEL[f.kind] ?? f.label }))} onChange={(v) => (ui.inspectorService.value = v)} />
    ) : null;
  return (
    <InspectorShell
      fkey={b.status === 'open' && chosen ? chosen.key : null}
      services={serviceTabs}
      header={<CardHeader icon={buildingIcon(b.kind)} thumb={thumbUrl(`b:${b.kind}`)} color={buildingCat(b.kind).color} level={chosen ? level : 0} title={name} subtitle={sub} onClose={close} />}
      overview={overview}
    />
  );
}

export function Inspector() {
  void ui.tick.value;
  const store = hud.store;
  const r = store?.replica;
  if (!store || !r) return null;
  const sel = ui.selection.value;
  if (!sel) return ui.grooming.value ? <GroomingPanel /> : <LivePanel />;
  const live = liveFacilities(r, store.map.features.village);
  if (sel.kind === 'lift') {
    const l = r.lifts.get(sel.id);
    if (l) return <LiftCard lift={l} live={live.find((f) => f.key === `L${l.id}`)} />;
  } else if (sel.kind === 'piste') {
    const p = r.pistes.get(sel.id);
    if (p) return <PisteCard piste={p} live={live.find((f) => f.key === `P${p.id}`)} />;
  } else if (sel.kind === 'groomer') {
    const g = r.groomers.get(sel.id);
    if (g) return <GroomerCard g={g} />;
  } else {
    const b = r.buildings.get(sel.id);
    if (b) return <BuildingCard b={b} live={live} />;
  }
  return <LivePanel />;
}

